import { ref } from "vue";
import { message } from "ant-design-vue";
import { merge, get } from "lodash";
import { convertToBytes } from "@/src/utils/utils.js";
import {
  fileSizeUnits,
  uploadDownloadAPITypes,
} from "@/src/constants/commonConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import ConversationService from "@/src/api/ConversationService.js";

const getDefaultConfig = () => ({
  api: uploadDownloadAPITypes.ATTACHMENTS,
  size: {
    value: 10,
    unit: fileSizeUnits.MB,
  },
  maxFiles: 1000,
  allowedTypes: [],
  messages: {
    totalSizeExceeded: "Attachments size should be less than {size} {unit}",
    maxFilesExceeded: "Maximum {maxFiles} files allowed",
    fileTypeNotAllowed: "File type {fileType} is not allowed",
    uploadError: "Failed to upload {fileName}",
  },
});

export function useFileUpload(userConfig = {}) {
  const config = merge({}, getDefaultConfig(), userConfig);
  const maxTotalSizeBytes = convertToBytes(config.size.value, config.size.unit);

  const attachments = ref([]);
  const isUploading = ref(false);
  let tempIdCounter = 0;

  const formatMessage = (message, params = {}) => {
    return message.replace(/{\w+(\.\w+)*}/g, (placeholder) => {
      const path = placeholder.slice(1, -1);
      return get(params, path, placeholder);
    });
  };

  const getTotalSize = (newFiles = []) => {
    const existingSize = attachments.value.reduce(
      (total, attachment) => total + attachment?.file_size || 0,
      0,
    );
    const newSize = Array.from(newFiles).reduce(
      (total, file) => total + file.size || 0,
      0,
    );

    return existingSize + newSize;
  };

  const mockUploadAttachment = async (formData) => {
    await new Promise((resolve) => setTimeout(resolve, 3000));
    const file = formData.get("files[]");
    return {
      success: true,
      files: [
        {
          id: "server-" + Date.now(),
          file_name: file.name,
          file_size: file.size,
          content_type: file.type,
          uploaded_by: 368,
          uploaded_at: new Date().toISOString(),
        },
      ],
    };
  };

  const initializeAttachments = (existingAttachments = []) => {
    attachments.value = existingAttachments.map((attachment) => ({
      ...attachment,
      isLoading: false,
    }));
  };

  const validateUpload = async (files = []) => {
    if (
      config?.maxFiles &&
      attachments.value.length + (files?.length || 0) > config.maxFiles
    ) {
      await message.error(
        formatMessage(config.messages.maxFilesExceeded, {
          maxFiles: config.maxFiles,
        }),
      );

      return false;
    }

    const totalSize = getTotalSize(files);
    if (totalSize > maxTotalSizeBytes) {
      await message.error(
        formatMessage(config.messages.totalSizeExceeded, {
          size: config.size.value,
          unit: config.size.unit,
        }),
      );
      return false;
    }

    if (config?.allowedTypes?.length || 0) {
      const allFilesValid = Array.from(files).every((file) => {
        const fileType = file.type.split("/")[1];

        if (!config?.allowedTypes?.includes(fileType)) {
          message.error(
            formatMessage(config.messages.fileTypeNotAllowed, {
              fileType: fileType || "unknown",
            }),
          );

          return false;
        }

        return true;
      });

      if (!allFilesValid) return false;
    }

    return true;
  };

  const uploadFiles = async (files = []) => {
    const filesForUpload = [...files];
    const validFileUploads = await validateUpload(filesForUpload);

    if (!validFileUploads) {
      return;
    }

    isUploading.value = true;

    const tempAttachments = filesForUpload.map((file) => {
      const tempId = `temp-${Date.now()}-${tempIdCounter++}`;
      const tempAttachment = {
        id: tempId,
        file_name: file.name,
        file_size: file.size,
        content_type: file.type,
        isLoading: true,
      };
      attachments.value.push(tempAttachment);
      return { tempId, file };
    });

    try {
      if (config?.api === uploadDownloadAPITypes.MARKETING_UPLOADS) {
        for (const { tempId, file } of tempAttachments) {
          const formData = new FormData();
          formData.append("file", file);

          const response = await DashboardService.uploadMarketingFile(formData);

          if (!response?.success) {
            throw new Error(
              "Upload failed: " + (response?.message || "Unknown error"),
            );
          }

          const attachmentIndex = attachments.value.findIndex(
            (a) => a.id === tempId,
          );
          if (attachmentIndex !== -1 && response.data) {
            attachments.value[attachmentIndex] = {
              id: response.data.key,
              file_name: file.name,
              file_size: response.data.size,
              content_type: file.type,
              url: response.data.url,
              isLoading: false,
            };
          }
        }
      } else {
        const formData = new FormData();
        filesForUpload.forEach((file) => {
          formData.append("files[]", file);
        });

        let response = {};

        if (config?.api === uploadDownloadAPITypes.CONVERSATION_ATTACHMENTS) {
          response = await DashboardService.uploadAttachment(formData);
        } else if (
          config?.api === uploadDownloadAPITypes.LIVE_CHAT_INBOX_ATTACHMENTS
        ) {
          response = await ConversationService.uploadAttachments(formData);
        } else {
          response = await DashboardService.uploadAttachmentV2(formData);
        }

        if (!response?.success) {
          throw new Error(
            "Upload failed: " + (response?.message || "Unknown error"),
          );
        }

        tempAttachments.forEach(({ tempId }, index) => {
          const attachmentIndex = attachments.value.findIndex(
            (a) => a.id === tempId,
          );
          if (attachmentIndex !== -1 && response.files[index]) {
            attachments.value[attachmentIndex] = {
              ...response.files[index],
              isLoading: false,
            };
          }
        });
      }

      return true;
    } catch (error) {
      tempAttachments.forEach(({ tempId, file }) => {
        const stillHasTempId = attachments.value.some((a) => a.id === tempId);
        
        if (stillHasTempId) {
          attachments.value = attachments.value.filter((a) => a.id !== tempId);
          message.error(
            formatMessage(config.messages.uploadError, {
              fileName: file.name,
            }),
          );
        }
      });
      console.error("Failed to upload files:", error);
      return false;
    } finally {
      isUploading.value = false;
    }
  };

  const removeAttachment = (attachmentOrId) => {
    const id = typeof attachmentOrId === 'object' ? attachmentOrId.id : attachmentOrId;
    attachments.value = attachments.value.filter((a) => a.id !== id);
  };

  const resetAttachments = () => {
    attachments.value = [];
    tempIdCounter = 0;
  };

  return {
    attachments,
    isUploading,
    uploadFiles,
    removeAttachment,
    resetAttachments,
    initializeAttachments,
    getTotalSize,
  };
}
